import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Card, Alert, Skeleton } from '@dreamtree-org/twreact-ui';
import { ShieldCheck } from 'lucide-react';
import { useAppApi } from '@/lib/api';

const errorOf = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || 'Something went wrong';

/**
 * An invited platform admin claims the account: /join?token=… (one-time, 7 days).
 * Sets a name and a password, then signs in. Password rules come from tenancy.
 */
export default function AdminJoin() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { fetchApi } = useAppApi();
  const [invite, setInvite] = useState(null);
  const [gone, setGone] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchApi('platformJoin', { token }, false)
      .then((r) => {
        setInvite(r.data);
        setName(r.data.name && r.data.name !== r.data.email ? r.data.name : '');
      })
      .catch((ex) => setGone(errorOf(ex)));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await fetchApi('platformJoinAccept', { token, name, password }, false);
      await fetchApi('platformLogin', { email: r.data.email, password }, false);
      window.location.replace('/');
    } catch (ex) {
      setErr(errorOf(ex));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-4 pt-safe pb-safe bg-[var(--app-bg)]">
      <Card className="w-full max-w-sm p-6 space-y-5">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-950">
            <ShieldCheck size={24} aria-hidden />
          </div>
          <h1 className="text-xl font-bold text-primary-600">MerchantPro Cloud</h1>
          <p className="text-sm font-medium">Join the platform team</p>
        </div>
        {gone ? (
          <Alert variant="error" title="This invitation can't be used">{gone}</Alert>
        ) : !invite ? (
          <Skeleton count={3} />
        ) : (
          <>
            <p className="text-sm text-center opacity-80">You were invited as <strong>{invite.role}</strong> ({invite.email}).</p>
            {err ? <Alert variant="error">{err}</Alert> : null}
            <form onSubmit={submit} className="space-y-4">
              <Input label="Your name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Input
                label="Choose a password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="At least 15 characters; a short phrase of unrelated words works well."
                required
              />
              <Button type="submit" fullWidth loading={busy} disabled={!name.trim() || password.length < 15}>Activate my account</Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
